<template>
  <component
    :is="tag"
    :class="['m-search-result-card']"
  >
    <div class="thumb-container">
      <component
        :is="linkTag"
        :href="href"
        :to="routerLink"
        ref="thumbLink"
        :name="title"
      >
        <img
          v-if="adaptiveThumbSrc"
          :src="adaptiveThumbSrc"
          :alt="title"
          class="img-full"
        >
        <slot
          v-else
          name="no-thumb"
        />
      </component>
    </div>
    <div class="info-container">
      <div>
        <component
          :is="linkTag"
          :href="href"
          :to="routerLink"
          class="item-title"
        >
          <Highlighter
            :text="title"
            :query="highlight"
          />
        </component>
        <div class="subtitle-legend-container">
          <div
            v-if="subtitle"
            class="item-subtitle"
          >
            <span>
              <Highlighter
                :text="subtitle"
                :query="highlight"
              />
              <span
                class="separator inline-separator"
                v-if="isCompactlayout"
              />
            </span>
          </div>
          <div
            v-if="legendList"
            class="item-legend"
          >
            <span
              v-for="(item, i) in legendListWithSeparator"
              :key="i"
              v-bind="omit(item, ['html'])"
              v-html="item.html"
            />
          </div>
        </div>
        <TextClamp
          v-if="useTextClamp && sanitizeDescription && !(isCompactlayout || isCardlayout)"
          :max-lines="maxDescriptionLines"
          class="item-description"
        >
          <Highlighter
            :text="sanitizeDescription"
            :query="highlight"
          />
        </TextClamp>
      </div>
    </div>
  </component>
</template>

<script>
import Highlighter from '../../../components-v2/highlighter/Highlighter.vue'
import TextClamp from '../../../components/Utils/TextClamp'
import omit from 'lodash/omit'

export default {
  name: 'SearchResultCard',
  components: {
    Highlighter,
    TextClamp
  },
  props: {
    tag: {
      type: String,
      default: 'li'
    },
    maxDescriptionLines: {
      type: Number,
      default: 3
    },
    href: {
      type: String,
      default: ''
    },
    thumbHeight: {
      type: Number,
      default: undefined
    },
    routerLink: {
      type: Object,
      default: () => {}
    },
    thumbPath: {
      type: String,
      default: ''
    },
    thumbPattern: {
      type: String,
      default: ''
    },
    thumbRatio: {
      type: Number,
      default: 341 / 192
    },
    thumbWidth: {
      type: Number,
      default: 341
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    legendList: {
      type: Array,
      default: () => []
    },
    description: {
      type: String,
      default: ''
    },
    highlight: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      omit
    }
  },
  computed: {
    linkTag () {
      if (this.routerLink) {
        return 'router-link'
      }

      return 'a'
    },
    defaultThumbHeight () {
      return this.thumbWidth * 1 / this.thumbRatio
    },
    adaptiveThumbSrc () {
      if (!this.thumbPattern) {
        return false
      }
      return this.thumbPattern
        .replace('{width}', this.thumbWidth)
        .replace('{height}', this.thumbHeight ?? this.defaultThumbHeight)
        .replace('{path}', this.thumbPath)
    },
    legendListWithSeparator () {
      if (!this.legendList) {
        return false
      }

      const items = [...this.legendList]
      const nbSeparators = items.length - 1
      for (let index = 0; index < nbSeparators; index++) {
        items.splice(index * 2 + 1, 0, { class: 'separator ' })
      }
      return items
    },
    isCompactlayout () {
      return this.$parent?.layout === 'compact' ?? false
    },
    isCardlayout () {
      return this.$parent?.layout === 'card' ?? false
    },
    useTextClamp () {
      return typeof window !== 'undefined'
    },
    sanitizeDescription () {
      if (this.description) {
        // remove HTML tags
        const withoutTags = this.description.replace(/(<([^>]+)>)/gi, '')
        // make sure HTML chars are well displayed
        const div = document.createElement('div')
        div.innerHTML = withoutTags
        return div.textContent
      }

      return false
    }
  }
}
</script>
<style scoped lang="sass">
@import '../../../sass/variables.sass'
.m-search-result-card
  &:hover
    color: unset
    .item-title
      color: $link-color
  .item-subtitle
    padding-right: 0.5em
    span
      white-space: nowrap
      .highlighter-wrapper
        white-space: normal
      .inline-separator
        margin-right: 0
        display: inline-block
        transform: translateY(18%)
</style>
